"use client";

import { Link, usePathname, useRouter } from "@/i18n/routing";
import { CORE_COLORS, CORE_FONT } from "@/lib/theme/core";
import { ROUTES } from "@/lib/routes";

export type AccountNavLabel =
  | "我的收藏"
  | "我的追蹤"
  | "個人資料"
  | "訂閱服務"
  | "通知設定";

const navItems: { label: AccountNavLabel; href: `/account/${string}` }[] = [
  { label: "我的收藏", href: "/account/bookmarks" },
  { label: "我的追蹤", href: "/account/following" },
  { label: "個人資料", href: "/account/profile" },
  { label: "訂閱服務", href: "/account/subscription" },
  { label: "通知設定", href: "/account/notifications" },
];

// The resting colour rides `--rest-fg` rather than `color`. It used to be set
// inline, which outranks every class — so the `hover:text-*` beside it could
// never apply and the sidebar tabs had no hover response at all.
// QA: "add hover effect to the sidebar tabs".
const linkStyle = (isActive: boolean): React.CSSProperties =>
  ({
    fontFamily: CORE_FONT.sans,
    fontSize: "15px",
    fontWeight: isActive ? 700 : 500,
    padding: "12px 0",
    borderBottom: `1px solid ${CORE_COLORS.dividerSoft}`,
    lineHeight: "20px",
    ["--rest-fg" as string]: isActive
      ? CORE_COLORS.brandGreen
      : CORE_COLORS.textPrimary,
    ["--green-dark" as string]: CORE_COLORS.brandGreenDark,
  }) as React.CSSProperties;

const NAV_ITEM_CLASS =
  "group inline-flex items-center justify-between text-[color:var(--rest-fg)] transition-colors hover:text-[color:var(--green-dark)]";

export function AccountNavContent({
  onNavigate,
}: {
  onNavigate?: () => void;
}) {
  const pathname = usePathname();
  const router = useRouter();

  /**
   * 登出 used to do nothing but close the mobile drawer — it was a
   * `type="button"` whose only handler was `onNavigate`.
   *
   * `replace`, not `push`: the account page must not sit in history behind the
   * logged-out home page, or Back walks straight into it. `refresh()` then
   * re-runs the server components so anything rendered for a signed-in reader
   * is rebuilt. QA: "when click on logout, refresh and redirect to home page".
   */
  const handleLogout = () => {
    onNavigate?.();
    router.replace(ROUTES.home);
    router.refresh();
  };

  return (
    <div className="flex flex-col gap-[32px]">
      <div className="flex flex-col gap-[16px]">
        <div
          className="inline-flex size-[72px] items-center justify-center rounded-full"
          style={{ backgroundColor: CORE_COLORS.surfaceMuted }}
          aria-label="Avatar"
        >
          <span
            style={{
              fontFamily: CORE_FONT.condensed,
              fontSize: "28px",
              fontWeight: 700,
              color: CORE_COLORS.textPrimary,
              letterSpacing: "0.2px",
              lineHeight: 1,
            }}
          >
            AC
          </span>
        </div>
        <div className="flex flex-col gap-[4px]">
          <p
            style={{
              fontFamily: CORE_FONT.sans,
              fontSize: "18px",
              fontWeight: 700,
              letterSpacing: "-0.1px",
              color: CORE_COLORS.textPrimary,
              lineHeight: "24px",
            }}
          >
            Arthur Chun
          </p>
          <p
            style={{
              fontFamily: CORE_FONT.sans,
              fontSize: "13px",
              color: CORE_COLORS.textSecondary,
              lineHeight: "18px",
            }}
          >
            arthurchun@hkej.com
          </p>
        </div>
      </div>

      <nav
        className="flex flex-col"
        style={{ borderTop: `1px solid ${CORE_COLORS.dividerBold}` }}
      >
        {navItems.map((item) => {
          const isActive = pathname === item.href;
          return (
            <Link
              key={item.label}
              href={item.href}
              onClick={onNavigate}
              className={NAV_ITEM_CLASS}
              style={linkStyle(isActive)}
            >
              {item.label}
              {isActive && (
                <span
                  aria-hidden
                  className="inline-block size-[6px] rounded-full"
                  style={{ backgroundColor: CORE_COLORS.brandGreen }}
                />
              )}
            </Link>
          );
        })}

        <button
          type="button"
          onClick={handleLogout}
          className={NAV_ITEM_CLASS}
          style={linkStyle(false)}
        >
          登出
        </button>
      </nav>
    </div>
  );
}
